// Globals
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';

// Components
import { StyledContentBlock, StyledPotentialPartnersWrapper } from '../../components/Styled';
import { PotentialPartnersList } from '../../components';

// Models
import { FeedbackStatus, FeedbackType, IFeedback } from '@/models/feedback.model';

// Modules
import { useGetFeedBacks } from '@/modules/Feedbacks/hooks';
import { changeFeedbackStatus, deleteFeedback, getFeedbacks } from '@/modules/Feedbacks';
import { usePermissions, Resource, Permission } from '@/modules/Permissions';
import { confirmAction } from '@/utils';

const PotentialPartnersContainer = () => {
  const { t } = useTranslation('feedbacks');
  const { list, isLoading, pagination, query } = useGetFeedBacks(FeedbackType.POTENTIAL_PARTNER);
  const dispatch = useAppDispatch();
  const { canManage, can } = usePermissions();
  // Potential partners are a feedback type; status change hits the feedback API (feedback.manage).
  const canManageFeedback = canManage(Resource.FEEDBACK);
  const canDeleteFeedback = can(Permission.FEEDBACK_DELETE);

  const handleChangePage = (page: number) => {
    dispatch(getFeedbacks({ ...query, type: FeedbackType.POTENTIAL_PARTNER, page }));
  };

  const handleChangeStatus = async ({ id, status }: { id: string; status: FeedbackStatus }) => {
    await dispatch(
      changeFeedbackStatus({
        id,
        type: FeedbackType.POTENTIAL_PARTNER,
        status,
      }),
    );
    dispatch(getFeedbacks({ ...query, type: FeedbackType.POTENTIAL_PARTNER, page: 1 }));
  };

  const handleDelete = (feedback: IFeedback) => {
    const page =
      list?.length === 1 && (pagination?.current_page ?? 1) > 1
        ? pagination!.current_page - 1
        : pagination?.current_page ?? 1;

    confirmAction(t('feedback_delete_title'), async () => {
      await dispatch(deleteFeedback({ id: feedback.id, type: FeedbackType.POTENTIAL_PARTNER })).unwrap();
      dispatch(getFeedbacks({ ...query, type: FeedbackType.POTENTIAL_PARTNER, page }));
    });
  };

  return (
    <StyledPotentialPartnersWrapper>
      <StyledContentBlock>
        <PotentialPartnersList
          isLoading={isLoading}
          currentPage={pagination?.current_page ?? 1}
          total={pagination?.total ?? 0}
          onPageChange={handleChangePage}
          onChangeStatus={canManageFeedback ? handleChangeStatus : undefined}
          partnersList={list ?? []}
          {...(canDeleteFeedback ? { onDelete: handleDelete } : {})}
        />
      </StyledContentBlock>
    </StyledPotentialPartnersWrapper>
  );
};

export default PotentialPartnersContainer;
